// apps/client/src/components/ChatGenerateStatus.tsx
import { JobStatus } from '@avanzu/types';
import { CheckCircle, Close, Error as ErrorIcon, Visibility } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useChatGenerate } from '../contexts/QueueContext';
import { useNavigate } from 'react-router-dom';

interface ChatGenerateStatusProps {
  sidebarOpen: boolean;
}

export const ChatGenerateStatus: React.FC<ChatGenerateStatusProps> = ({ sidebarOpen }) => {
  const { currentJob, clearGeneration } = useChatGenerate();
  const navigate = useNavigate();

  if (!currentJob) {
    return null;
  }

  const { status, errorMessage, projectId } = currentJob;

  const isInProgress = status === JobStatus.IN_PROGRESS || status === JobStatus.QUEUED;
  const isComplete = status === JobStatus.COMPLETED;
  const isFailed = status === JobStatus.FAILED;

  const handleViewProject = () => {
    if (projectId) {
      clearGeneration();
      navigate(`/app/projects/${projectId}`);
    }
  };

  // Collapsed view - just show circular progress centered
  if (!sidebarOpen) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 2,
          ml: 2,
        }}
      >
        <Tooltip
          title={
            isComplete
              ? 'Project generated! Click to view'
              : isInProgress
                ? 'Generating project...'
                : 'Generation failed'
          }
          placement="right"
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: isComplete ? 'success.light' : isFailed ? 'error.light' : 'action.hover',
              cursor: isComplete ? 'pointer' : 'default',
              transition: 'all 0.2s',
              ...(isComplete && {
                '&:hover': {
                  bgcolor: 'success.main',
                },
              }),
            }}
            onClick={isComplete ? handleViewProject : undefined}
          >
            {isInProgress && <CircularProgress size={24} />}
            {isComplete && <CheckCircle sx={{ fontSize: 24, color: 'success.main' }} />}
            {isFailed && <ErrorIcon sx={{ fontSize: 24, color: 'error.main' }} />}
          </Box>
        </Tooltip>
      </Box>
    );
  }

  // Expanded view - show full details
  return (
    <Box sx={{ px: 1, py: 2 }}>
      <Box sx={{ p: 1.5 }}>
        <Stack spacing={1.5}>
          {/* Header */}
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1}>
              {isInProgress && <CircularProgress size={16} />}
              {isComplete && <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />}
              {isFailed && <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />}
              <Typography variant="caption" fontWeight={600}>
                {isInProgress && 'Generating Project...'}
                {isComplete && 'Project Generated!'}
                {isFailed && 'Generation Failed'}
              </Typography>
            </Stack>
            <IconButton size="small" onClick={clearGeneration} sx={{ ml: 0.5 }}>
              <Close fontSize="small" />
            </IconButton>
          </Stack>

          {/* Progress bar (indeterminate) */}
          {isInProgress && (
            <>
              <LinearProgress variant="indeterminate" sx={{ height: 4, borderRadius: 2 }} />
              <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                AI is analyzing your conversation...
              </Typography>
            </>
          )}

          {/* Success message with button */}
          {isComplete && projectId && (
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<Visibility />}
              onClick={handleViewProject}
              fullWidth
              sx={{ mt: 0.5 }}
            >
              View Project
            </Button>
          )}

          {/* Error message */}
          {isFailed && errorMessage && (
            <Typography variant="caption" color="error.main" fontSize="0.7rem">
              {errorMessage}
            </Typography>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default ChatGenerateStatus;
